import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Plus, Loader2, Pencil, Trash2, MessageCircle } from "lucide-react";
import { PageHead, Empty, Field, inputCls, btnPrimary, btnGhost } from "@/components/erp/ErpUi";
import { maskCnpj, validCnpj, onlyDigits, maskPhone } from "@/components/CustomerProfileFields";

const EMPTY = { name: "", trade_name: "", cnpj: "", contact: "", phone: "", email: "", city: "", uf: "", notes: "", kind: "fornecedor" };

function SupplierDialog({ open, onOpenChange, supplier, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const last = useRef("");
  useEffect(() => { if (open) { setF(supplier ? { ...EMPTY, ...supplier, cnpj: maskCnpj(supplier.cnpj || "") } : EMPTY); last.current = onlyDigits(supplier?.cnpj); } }, [open, supplier]);
  useEffect(() => {
    const d = onlyDigits(f.cnpj);
    if (d.length !== 14 || !validCnpj(d) || last.current === d) return;
    last.current = d; setLooking(true);
    axios.get(`https://brasilapi.com.br/api/cnpj/v1/${d}`, { timeout: 8000, withCredentials: false })
      .then(({ data }) => setF((p) => ({ ...p, name: data.razao_social || p.name, trade_name: data.nome_fantasia || p.trade_name,
        phone: p.phone || maskPhone(data.ddd_telefone_1 || ""), email: p.email || (data.email || "").toLowerCase(), city: data.municipio || p.city, uf: data.uf || p.uf })))
      .catch(() => {}).finally(() => setLooking(false));
  }, [f.cnpj]);
  const set = (k, fn = (v) => v) => (e) => setF((p) => ({ ...p, [k]: fn(e.target.value) }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...f, cnpj: onlyDigits(f.cnpj), phone: onlyDigits(f.phone) };
      supplier ? await api.patch(`/erp/suppliers/${supplier.id}`, body) : await api.post("/erp/suppliers", body);
      toast.success("Fornecedor salvo"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const bad = onlyDigits(f.cnpj).length === 14 && !validCnpj(f.cnpj);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="supplier-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{supplier ? "Editar fornecedor" : "Novo fornecedor"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label={looking ? "CNPJ (buscando…)" : "CNPJ"} className="col-span-2">
            <Input data-testid="supplier-cnpj" value={f.cnpj} onChange={set("cnpj", maskCnpj)} inputMode="numeric" className={inputCls} />
            {bad && <p className="text-xs text-red-300 mt-1">CNPJ inválido</p>}
          </Field>
          <Field label="Tipo" className="col-span-2">
            <select data-testid="supplier-kind" value={f.kind || "fornecedor"} onChange={set("kind")} className="h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 text-base sm:text-sm text-zinc-100">
              <option value="fornecedor">Fornecedor de peças</option>
              <option value="prestador">Prestador de serviço (terceirizado)</option>
            </select>
          </Field>
          <Field label="Razão social / nome *" className="col-span-2"><Input data-testid="supplier-name" value={f.name} onChange={set("name")} className={inputCls} /></Field>
          <Field label="Nome fantasia" className="col-span-2"><Input data-testid="supplier-trade" value={f.trade_name} onChange={set("trade_name")} className={inputCls} /></Field>
          <Field label="Contato"><Input data-testid="supplier-contact" value={f.contact} onChange={set("contact")} className={inputCls} /></Field>
          <Field label="Telefone"><Input data-testid="supplier-phone" value={f.phone} onChange={set("phone", maskPhone)} inputMode="tel" className={inputCls} /></Field>
          <Field label="E-mail" className="col-span-2"><Input data-testid="supplier-email" value={f.email} onChange={set("email")} type="email" className={inputCls} /></Field>
          <Field label="Cidade"><Input data-testid="supplier-city" value={f.city} onChange={set("city")} className={inputCls} /></Field>
          <Field label="UF"><Input data-testid="supplier-uf" value={f.uf} onChange={set("uf", (v) => v.toUpperCase().slice(0, 2))} className={inputCls} /></Field>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="supplier-save" type="submit" disabled={busy || !f.name.trim()} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function OwnerSuppliers() {
  const { user } = useAuth();
  const scopeAll = user?.shop_id === "all";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState({ open: false, supplier: null });
  const load = useCallback(() => api.get("/erp/suppliers", { params: { q } }).then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e))), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const remove = async (s) => {
    if (!window.confirm(`Excluir ${s.name}?`)) return;
    try { await api.delete(`/erp/suppliers/${s.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={Truck} kicker="Compras" title="Fornecedores">
        <Button data-testid="supplier-new-btn" disabled={scopeAll} className={btnPrimary} onClick={() => setEdit({ open: true, supplier: null })}><Plus className="h-4 w-4 mr-1" /> Novo fornecedor</Button>
      </PageHead>
      <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou CNPJ" className={inputCls} />
      {!items ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : items.length === 0 ? <Empty>Nenhum fornecedor.</Empty> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((s) => {
            const d = onlyDigits(s.phone);
            return (
              <div key={s.id} data-testid={`supplier-card-${s.id}`} className="rounded-2xl border border-card2-border bg-card2 p-4">
                <div className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-zinc-100 truncate">{s.trade_name || s.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{s.name}</div>
                    {s.kind === "prestador" && <span data-testid={`supplier-kind-${s.id}`} className="mt-1 inline-block rounded-full border border-orange-500 bg-orange-500/15 px-2 text-[10px] font-bold uppercase text-orange-300">Prestador de serviço</span>}
                  </div>
                  <div className="flex shrink-0">
                    <button data-testid={`supplier-edit-${s.id}`} onClick={() => setEdit({ open: true, supplier: s })} className="p-1.5 text-zinc-400 hover:text-green-neon"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`supplier-delete-${s.id}`} onClick={() => remove(s)} className="p-1.5 text-zinc-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="text-xs text-zinc-400 mt-2 space-y-0.5">
                  {s.cnpj && <div>CNPJ {maskCnpj(s.cnpj)}</div>}
                  {s.contact && <div>Contato: {s.contact}</div>}
                  {(s.city || s.uf) && <div>{[s.city, s.uf].filter(Boolean).join(" / ")}</div>}
                </div>
                {d && <a data-testid={`supplier-wa-${s.id}`} href={`https://wa.me/${d.length >= 12 ? d : `55${d}`}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 h-8 px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase"><MessageCircle className="h-3.5 w-3.5" /> {maskPhone(d)}</a>}
              </div>
            );
          })}
        </div>
      )}
      <SupplierDialog open={edit.open} supplier={edit.supplier} onOpenChange={(o) => setEdit((p) => ({ ...p, open: o }))} onSaved={load} />
    </div>
  );
}
